import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

export default function Auth() {
  const { user, loading: authLoading, signIn, signUp, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<"login" | "signup" | "recover">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Clean messages on mode change
  const changeMode = (newMode: "login" | "signup" | "recover") => {
    setMode(newMode);
    setError(null);
    setSuccess(null);
    setPassword("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Basic Validation
    if (!email) {
      setError(mode === "recover" ? "E-mail é obrigatório." : "E-mail e senha são obrigatórios.");
      return;
    }

    if (mode !== "recover" && !password) {
      setError("E-mail e senha são obrigatórios.");
      return;
    }

    if (mode !== "recover" && password.length < 6) {
      setError("Senha deve ter pelo menos 6 caracteres.");
      return;
    }

    setLoading(true);

    try {
      if (mode === "login") {
        await signIn(email, password);
        setSuccess("Login realizado com sucesso.");
        // Redirection will be handled by the useEffect
      } else if (mode === "signup") {
        await signUp(email, password);
        setSuccess("Conta criada. Verifique seu e-mail, se a confirmação estiver ativada.");
        setPassword("");
      } else if (mode === "recover") {
        await resetPassword(email);
        setSuccess("Enviamos as instruções de recuperação para seu e-mail.");
      }
    } catch (err: any) {
      console.error("[Auth] Authentication error:", err);
      if (mode === "login" || mode === "signup") {
        setError("Não foi possível autenticar. Verifique seus dados.");
      } else {
        setError(err.message || "Não foi possível processar a recuperação. Verifique seus dados.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-tr from-purple-100 via-[#F3F4F6] to-purple-50 p-4 font-sans select-none">
      <div className="w-full max-w-md space-y-6 animate-in fade-in zoom-in-95 duration-500">
        
        {/* App Logo Header */}
        <div className="flex flex-col items-center text-center gap-2 mb-2">
          <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-white font-bold text-3xl shadow-lg shadow-primary/20 hover:scale-105 transition-transform duration-300">
            T
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-black text-gray-950 tracking-tight">TEF Canada</span>
            <span className="text-xs font-bold text-primary uppercase tracking-widest -mt-1">Trainer</span>
          </div>
        </div>

        {/* Main Card */}
        <Card className="border-gray-200/80 shadow-xl bg-white/95 backdrop-blur-md rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-purple-100">
          <CardHeader className="space-y-1.5 pb-4 text-center border-b border-gray-100/50">
            <CardTitle className="text-xl font-bold tracking-tight text-gray-900">
              {mode === "login" && "Entrar na minha conta"}
              {mode === "signup" && "Criar minha conta"}
              {mode === "recover" && "Esqueci minha senha"}
            </CardTitle>
            <CardDescription className="text-xs text-gray-500">
              {mode === "login" && "Insira seus dados para acessar a plataforma"}
              {mode === "signup" && "Cadastre-se gratuitamente para começar a estudar"}
              {mode === "recover" && "Insira seu e-mail cadastrado para redefinir sua senha"}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Feedback Alerts */}
              {error && (
                <div className="flex items-start gap-2 p-3.5 rounded-xl bg-red-50 border border-red-150 text-red-700 text-sm animate-in fade-in duration-200">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-600" />
                  <span className="font-medium leading-relaxed">{error}</span>
                </div>
              )}

              {success && (
                <div className="flex items-start gap-2 p-3.5 rounded-xl bg-emerald-50 border border-emerald-150 text-emerald-800 text-sm animate-in fade-in duration-200">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-600" />
                  <span className="font-medium leading-relaxed">{success}</span>
                </div>
              )}

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="exemplo@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="rounded-xl border-gray-200 bg-gray-50/50 focus-visible:bg-white focus-visible:ring-primary focus-visible:border-primary/50 transition-all duration-200 h-10"
                />
              </div>

              {/* Password Field (Only shown in Login & Signup) */}
              {mode !== "recover" && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password" className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Senha
                    </Label>
                    {mode === "login" && (
                      <button
                        type="button"
                        onClick={() => changeMode("recover")}
                        className="text-xs font-semibold text-primary hover:text-primary/80 hover:underline transition-all"
                        disabled={loading}
                      >
                        Esqueceu?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      className="rounded-xl border-gray-200 bg-gray-50/50 focus-visible:bg-white focus-visible:ring-primary focus-visible:border-primary/50 transition-all duration-200 pr-10 h-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-primary hover:bg-primary/95 text-white font-semibold rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] transition-all duration-200"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  <>
                    {mode === "login" && "Entrar"}
                    {mode === "signup" && "Criar Conta"}
                    {mode === "recover" && "Recuperar Senha"}
                  </>
                )}
              </Button>
            </form>
          </CardContent>

          {/* Footer Navigation */}
          <CardFooter className="flex flex-col gap-3 pt-2 pb-6 px-6 text-center border-t border-gray-100/50 bg-gray-50/30">
            {mode === "login" && (
              <p className="text-xs text-gray-500 font-medium">
                Não tem uma conta?{" "}
                <button
                  type="button"
                  onClick={() => changeMode("signup")}
                  className="font-bold text-primary hover:text-primary/80 hover:underline transition-all"
                  disabled={loading}
                >
                  Cadastre-se agora
                </button>
              </p>
            )}

            {mode === "signup" && (
              <p className="text-xs text-gray-500 font-medium">
                Já possui uma conta?{" "}
                <button
                  type="button"
                  onClick={() => changeMode("login")}
                  className="font-bold text-primary hover:text-primary/80 hover:underline transition-all"
                  disabled={loading}
                >
                  Entrar
                </button>
              </p>
            )}

            {mode === "recover" && (
              <button
                type="button"
                onClick={() => changeMode("login")}
                className="text-xs font-bold text-primary hover:text-primary/80 hover:underline transition-all flex items-center gap-1 mx-auto"
                disabled={loading}
              >
                Voltar para o Login
              </button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
